import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "./router.js";
import { useAuth } from "./auth.js";
import { AppShell } from "./components/app-shell.js";
import { LoadingBlock } from "./components/ui.js";
import { LoginPage } from "./pages/login.js";
import {
  AcceptInvitationPage,
  ForgotPasswordPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "./pages/auth-flows.js";

const OverviewPage = lazy(() =>
  import("./pages/overview.js").then((module) => ({ default: module.OverviewPage })),
);
const OrganizationsPage = lazy(() =>
  import("./pages/organizations.js").then((module) => ({ default: module.OrganizationsPage })),
);
const OrganizationDetailPage = lazy(() =>
  import("./pages/organization-detail.js").then((module) => ({
    default: module.OrganizationDetailPage,
  })),
);
const SitesPage = lazy(() =>
  import("./pages/sites.js").then((module) => ({ default: module.SitesPage })),
);
const SiteDetailPage = lazy(() =>
  import("./pages/site-detail.js").then((module) => ({ default: module.SiteDetailPage })),
);
const DataTablePage = lazy(() =>
  import("./pages/data-table.js").then((module) => ({ default: module.DataTablePage })),
);
const RulesPage = lazy(() =>
  import("./pages/rules.js").then((module) => ({ default: module.RulesPage })),
);
const AiProvidersPage = lazy(() =>
  import("./pages/ai-providers.js").then((module) => ({ default: module.AiProvidersPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/settings.js").then((module) => ({ default: module.SettingsPage })),
);
const AccountSecurityPage = lazy(() =>
  import("./pages/account-security.js").then((module) => ({ default: module.AccountSecurityPage })),
);
const OperationsPage = lazy(() =>
  import("./pages/operations.js").then((module) => ({ default: module.OperationsPage })),
);
const UsersPage = lazy(() =>
  import("./pages/users.js").then((module) => ({ default: module.UsersPage })),
);
const BillingPage = lazy(() =>
  import("./pages/billing.js").then((module) => ({ default: module.BillingPage })),
);

function ProtectedApp() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="boot-screen">
        <LoadingBlock />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  const platform =
    user.platformRoleCode === "platform_super_admin" ||
    user.platformRoleCode === "platform_security_analyst";
  return (
    <AppShell>
      <Suspense fallback={<LoadingBlock />}>
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/sites/:id" element={<SiteDetailPage />} />
          <Route path="/account/security" element={<AccountSecurityPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route
            path="/events"
            element={platform ? <DataTablePage kind="events" /> : <Navigate to="/" replace />}
          />
          <Route
            path="/users"
            element={
              user.platformRoleCode === "platform_super_admin" ? (
                <UsersPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/risk"
            element={platform ? <DataTablePage kind="risk" /> : <Navigate to="/" replace />}
          />
          <Route path="/rules" element={platform ? <RulesPage /> : <Navigate to="/" replace />} />
          <Route
            path="/ai"
            element={platform ? <AiProvidersPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/settings"
            element={
              user.platformRoleCode === "platform_super_admin" ? (
                <SettingsPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/operations"
            element={platform ? <OperationsPage /> : <Navigate to="/" replace />}
          />
          <Route
            path="/audit"
            element={platform ? <DataTablePage kind="audit" /> : <Navigate to="/" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="*" element={<ProtectedApp />} />
    </Routes>
  );
}
